import { useEffect, useState } from 'react';
import config from '../config';
import type { AppData } from '../types';

const STORAGE_KEY = 'avocat_app_data';

/**
 * Hook for managing data persistence (localStorage or cloud)
 */
export function useStorage() {
  const load = async (): Promise<AppData | null> => {
    try {
      if (config.storageMode === 'localStorage') {
        try {
          const data = localStorage.getItem(STORAGE_KEY);
          return data ? JSON.parse(data) : null;
        } catch (storageError) {
          console.error('Error accessing localStorage:', storageError);
          return null;
        }
      } else {
        // Cloud storage mode - fetch from API
        const res = await fetch(`${config.apiUrl}/data/app`);
        if (!res.ok) return null;
        const json = await res.json();
        return json.content ? JSON.parse(json.content) : null;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      return null;
    }
  };

  const save = async (data: AppData): Promise<void> => {
    try {
      const jsonStr = JSON.stringify(data);
      if (config.storageMode === 'localStorage') {
        try {
          localStorage.setItem(STORAGE_KEY, jsonStr);
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }
      } else {
        await fetch(`${config.apiUrl}/data/app`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: jsonStr }),
        });
      }
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  return { load, save };
}

/**
 * Hook for managing form state with validation
 */
export function useForm<T extends Record<string, any>>(
  initialValues: T,
  onSubmit?: (values: T) => void
) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const handleChange = (field: keyof T, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const reset = () => setValues(initialValues);

  const validate = (validators: Partial<Record<keyof T, (val: any) => string | null>>) => {
    const newErrors: Partial<Record<keyof T, string>> = {};
    Object.entries(validators).forEach(([field, validator]) => {
      const error = (validator as any)(values[field as keyof T]);
      if (error) newErrors[field as keyof T] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = (validators?: Partial<Record<keyof T, (val: any) => string | null>>) => {
    if (validators) {
      if (validate(validators) && onSubmit) onSubmit(values);
    } else if (onSubmit) {
      onSubmit(values);
    }
  };

  return { values, errors, handleChange, reset, validate, submit };
}

/**
 * Hook for tracking authentication state
 */
export function useAuth(accessCode: string) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const login = (code: string) => {
    if (code === accessCode) {
      setIsAuthenticated(true);
      try {
        localStorage.setItem('auth_token', code);
      } catch (error) {
        console.error('Error saving auth token:', error);
      }
      return true;
    }
    setAttempts((prev) => prev + 1);
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    try {
      localStorage.removeItem('auth_token');
    } catch (error) {
      console.error('Error removing auth token:', error);
    }
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token === accessCode) {
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error reading auth token:', error);
    }
  }, []);

  return { isAuthenticated, login, logout, attempts };
}

/**
 * Hook for debounced state updates
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook for tracking previous value
 */
export function usePrevious<T>(value: T): T | undefined {
  const [previous, setPrevious] = useState<T>();

  useEffect(() => {
    setPrevious(value);
  }, [value]);

  return previous;
}
